<template>
    <div class="message-list-item" :class="{ disabled: disabled }" @click="message_clickHandler">
        <div class="avatar-container">
        <Avatar
            :label="initials"
            size="xlarge"
                :class="{ read: !unreadCount }"
            :style="selfStyle"
            v-badge.success="unreadCount"
        />
            
            <div 
                v-if="showReadStatusIndicator" 
                :class="['read-status-indicator', readStatusIndicatorClass]"
                :title="readStatusTooltip"
            >
                <i class="pi pi-circle-fill"></i>
            </div>
            <Menu 
                ref="avatarMenu"
                :model="avatarMenuItems"
                :popup="true"
                appendTo="body"
            />
        </div>
        <div class="message-body">
            <div class="display-flex align-items-center gap-2">
                <span class="profile-name">{{ firstName }} {{ lastName }}</span>
                <span v-if="roleLabel" :class="roleBadgeClass">{{ roleLabel }}</span>
            </div>
            <span :class="selfClass">{{ latestMessageText }}</span>
        </div>
        <div class="message-actions">
        <span class="message-sent">{{ sentDate }}</span>
            <Button
                v-if="!isAI"
                icon="pi pi-ellipsis-v"
                class="p-button-text p-button-sm menu-button"
                @click="menuButton_clickHandler"
                :aria-label="'Menu for ' + firstName + ' ' + lastName"
            />
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { createNamespacedHelpers } from 'vuex'
import Menu from 'primevue/menu'
import Button from 'primevue/button'

const portalVuex = createNamespacedHelpers('portal')

export default {
    components: {
        Menu,
        Button,
    },
    data() {
        return {}
    },
    props: {
        channel: {
            type: Object,
            required: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    mounted() {},
    computed: {
        ...portalVuex.mapGetters(['userId']),
        selfClass() {
            return `message-content ${this.unreadCount > 0 ? 'unread' : ''}`
        },
        selfStyle() {
            if (this.isAI) {
                return { backgroundColor: '#22C55E', color: '#ffffff' } // Green for AI
            }
            if (this.isAssignedToMe) {
                return { backgroundColor: '#3B82F6', color: '#ffffff' } // Blue for Care Coordinator (Primary)
            }
            if (this.isWatching) {
                return { backgroundColor: '#F59E0B', color: '#ffffff' } // Orange for Watcher (Warning)
            }
            return { backgroundColor: '#6B7280', color: '#ffffff' } // Gray for Not Participating
        },
        isWatching() {
            return this.channel?.participants?.some(p => 
                p.principal === this.userId && p.role === 'WATCHER'
            )
        },
        initials() {
            return this.isAI ? 'AI' : this.firstName.charAt(0) + this.lastName.charAt(0)
        },
        firstName() {
            return this.channel?.patientFirstName
        },
        lastName() {
            return this.channel?.patientLastName
        },
        latestMessageText() {
            return this.channel?.lastMessage?.message
        },
        isAssignedToMe() {
            return this.channel?.owner === this.userId
        },
        isAI() {
            return this.channel?.isAI
        },
        unreadCount() {
            const count = this.channel?.unreadMessages
            // Return undefined if count is not a valid number > 0 (hides badge)
            return (typeof count === 'number' && count > 0) ? count : undefined
        },
        showReadStatusIndicator() {
            // Only show for assigned care coordinator and non-AI channels
            // AND only if the assigned CC has unread messages
            return this.isAssignedToMe && !this.isAI && this.channel?.lastMessage && this.unreadCount > 0
        },
        readStatusIndicatorClass() {
            // Red if read by others, green if unread by anyone
            return this.isReadByOthers ? 'read-by-others' : 'unread-by-all'
        },
        readStatusTooltip() {
            return this.isReadByOthers 
                ? 'Message read by other team members' 
                : 'Message unread by team members'
        },
        isReadByOthers() {
            if (!this.channel?.participants || !this.channel?.lastMessage) return false
            
            // Check if any participant other than the assigned CC has read the latest message
            const lastMessageDate = this.channel.lastMessage.lastModifiedDate
            return this.channel.participants.some(participant => {
                return participant.principal !== this.userId && 
                       participant.role !== 'PATIENT' &&
                       participant.readUntil && 
                       new Date(participant.readUntil) >= new Date(lastMessageDate)
            })
        },
        roleLabel() {
            if (this.isAI) return 'AI'
            if (this.isAssignedToMe) return 'Managing'
            if (this.isWatching) return 'Watching'
            return null
        },
        roleBadgeClass() {
            if (this.isAI) return 'px-2 py-1 border-round text-xs font-medium bg-green-100 text-green-700'
            if (this.isAssignedToMe) return 'px-2 py-1 border-round text-xs font-medium bg-blue-100 text-blue-700'
            if (this.isWatching) return 'px-2 py-1 border-round text-xs font-medium bg-orange-100 text-orange-700'
            return 'px-2 py-1 border-round text-xs bg-gray-100 text-gray-600'
        },
        avatarMenuItems() {
            if (this.isAI) {
                console.log('Returning empty menu items - AI channel')
                return []
            }
            
            const myParticipant = this.channel?.participantsMap?.get(this.userId)
            const hasUnreadMessages = myParticipant?.unreadMessagesCount > 0
            const isWatching = this.isWatching
            const isParticipant = !!myParticipant
            const hasNonWatcherRole = isParticipant && myParticipant?.role !== 'WATCHER'

            const items = []

            // Read/Unread functionality - available for any channel you're a participant in
            if (isParticipant) {
                items.push({
                    label: hasUnreadMessages ? 'Mark as Read' : 'Mark as Unread',
                    icon: hasUnreadMessages ? 'pi pi-check' : 'pi pi-times',
                    command: () => this.toggleReadStatus()
                })
            }

            // Watch/Unwatch functionality - only show if user is not already assigned with another role
            // If user is already a CARE_COORDINATOR or other role, don't show watch options
            if (!hasNonWatcherRole) {
                if (isWatching) {
                    items.push({
                        label: 'Stop Watching',
                        icon: 'pi pi-eye-slash',
                        command: () => this.unwatchChannel()
                    })
                } else {
                    items.push({
                        label: 'Watch Channel',
                        icon: 'pi pi-eye',
                        command: () => this.watchChannel()
                    })
                }
            }

            // Invite team member - only for channels you own
            if (this.isAssignedToMe) {
                items.push({
                    label: 'Invite Team Member',
                    icon: 'pi pi-user-plus',
                    command: () => this.showInviteDialog()
                })
            }

            return items
        },
        messageReaders() {
            if (!this.channel?.participants || !this.channel?.lastMessage) return []
            
            const lastMessageDate = this.channel.lastMessage.lastModifiedDate
            const careTeamMembers = this.$store.getters['portal/careTeamMembers'] || []
            
            return this.channel.participants
                .filter(participant => {
                    return participant.principal !== this.userId && 
                           participant.role !== 'PATIENT' &&
                           participant.readUntil && 
                           new Date(participant.readUntil) >= new Date(lastMessageDate)
                })
                .map(participant => {
                    const user = careTeamMembers.find(member => member.id === participant.principal)
                    return {
                        userId: participant.principal,
                        name: user ? `${user.firstName} ${user.lastName}` : 'Unknown',
                        initials: user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}` : '?'
                    }
                })
                .slice(0, 3) // Limit to 3 bubbles max
        },
        sentDate() {
            if (this.channel.lastMessage) {
                const messageSentTime = moment(this.channel.lastMessage.lastModifiedDate)
                const currentTime = moment()

                const hoursDifference = currentTime.diff(messageSentTime, 'hours')

                if (hoursDifference >= 24) {
                    return messageSentTime.format('MMM DD')
                } else {
                    messageSentTime.locale('en', {
                        relativeTime: {
                            future: 'in %s',
                            past: '%s ago',
                            s: 'a few seconds',
                            ss: '%ds',
                            m: '1m',
                            mm: '%dm',
                            h: '1h',
                            hh: '%dh',
                            d: '1d',
                            dd: '%dd',
                        },
                    })
                    return messageSentTime.fromNow(true)
                }
            }
            return ''
        },
    },
    methods: {
        message_clickHandler() {
            if (this.disabled) return
            this.$emit('openChannel', this.channel)
        },
        menuButton_clickHandler(event) {
            event.stopPropagation() // Prevent opening the chat thread
            if (this.isAI) return // Only prevent AI channels
            this.$refs.avatarMenu.toggle(event)
        },
        async toggleReadStatus() {
            if (!this.channel?.id) {
                console.log('No channel ID, returning')
                return
            }
            
            const myParticipant = this.channel.participantsMap?.get(this.userId)
            const hasUnreadMessages = myParticipant?.unreadMessagesCount > 0
            

            try {
                const messages = await this.$store.dispatch('messaging/fetchMessagesForChannel', {
                    channelId: this.channel.id, 
                    additional: false 
                })
                
                if (!messages || messages.length === 0) {
                    console.log('No messages found in channel')
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'No messages found to mark as read/unread',
                        life: 3000
                    })
                    return
                }
                
                // Get the latest message (first in the array since they're sorted newest first)
                const latestMessage = messages[0]

                if (!latestMessage.id) {
                    console.log('Latest message has no ID')
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Message ID not found',
                        life: 3000
                    })
                    return
                }

                if (hasUnreadMessages) {
                    const result = await this.$store.dispatch('messaging/readMessage', { 
                        message: latestMessage 
                    })
                    this.$toast.add({
                        severity: 'success',
                        summary: 'Marked as Read',
                        detail: 'Channel marked as read',
                        life: 3000
                    })
                } else {
                    const result = await this.$store.dispatch('messaging/unreadMessage', { 
                        message: latestMessage 
                    })
                    this.$toast.add({
                        severity: 'info',
                        summary: 'Marked as Unread',
                        detail: 'Channel marked as unread',
                        life: 3000
                    })
                }
            } catch (error) {
                console.error('Error toggling read status:', error)
                console.error('Error details:', error.message, error.stack)
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update message status',
                    life: 3000
                })
            }
        },
        async watchChannel() {
            try {
                const result = await this.$store.dispatch('messaging/watchChannel', { 
                    channel: this.channel 
                })
                this.$toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Now watching this channel',
                    life: 3000
                })
            } catch (error) {
                console.error('Error watching channel:', error)
                console.error('Error details:', error.message, error.stack)
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to watch channel',
                    life: 3000
                })
            }
        },
        async unwatchChannel() {
            try {
                const message = await this.promptForMessage('Why are you stopping to watch this channel?')
                const result = await this.$store.dispatch('messaging/unwatchChannel', { 
                    channel: this.channel,
                    message 
                })
                this.$toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Stopped watching this channel',
                    life: 3000
                })
            } catch (error) {
                console.error('Error unwatching channel:', error)
                console.error('Error details:', error.message, error.stack)
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to unwatch channel',
                    life: 3000
                })
            }
        },
        showInviteDialog() {
            this.$emit('showInviteDialog', this.channel)
        },
        async promptForMessage(title) {
            return new Promise((resolve) => {
                this.$confirm.require({
                    message: title,
                    header: 'Optional Message',
                    acceptLabel: 'Continue',
                    rejectLabel: 'Cancel',
                    accept: () => resolve(''),
                    reject: () => resolve('')
                })
            })
        },
    },
    watch: {},
}
</script>
<style lang="scss" scoped>
.message-list-item {
    max-width: calc(100% - 4px);
    padding: 12px 0;
    margin-right: 4px;
    display: flex;
    flex-direction: row;
    align-items: center;

    &:hover {
        background-color: #e9f4ff;
        cursor: pointer;
    }

    .avatar-container {
        position: relative;
        margin-right: 16px;
        z-index: 1;

        .p-avatar {
        padding: 12px;
            cursor: pointer;

        &.read ::v-deep .p-badge {
            display: none;
            }
        }

        .read-status-indicator {
            position: absolute;
            bottom: 2px;
            right: 2px;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            border: 2px solid white;
            display: flex;
            align-items: center;
            justify-content: center;

            .pi {
                font-size: 8px;
            }

            &.read-by-others {
                background-color: #dc3545;
                color: white;
            }

            &.unread-by-all {
                background-color: #28a745;
                color: white;
            }
        }

        .user-read-bubbles {
            position: absolute;
            bottom: -8px;
            right: -8px;
            display: flex;
            gap: 2px;

            .user-read-bubble {
                width: 16px;
                height: 16px;
                border-radius: 50%;
                background-color: #007bff;
                color: white;
                font-size: 8px;
                display: flex;
                align-items: center;
                justify-content: center;
                border: 1px solid white;
                font-weight: bold;
            }
        }
    }

    .message-body {
        min-width: 210px;
        max-width: 210px;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        flex: 1;

        .profile-name {
            font-weight: 500;
            font-size: 14px;
        }

        .message-content {
            width: 100%;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;

            &.unread {
                font-weight: 500;
            }
        }
    }

    .message-actions {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 4px;
        min-width: 60px;

    .message-sent {
        text-align: end;
            font-size: 12px;
        }

        .menu-button {
            opacity: 0;
            transition: opacity 0.2s ease;
            padding: 4px;
            min-width: 24px;
            width: 24px;
            height: 24px;

            ::v-deep .p-button-icon {
                font-size: 12px;
            }
        }
    }

    &:hover .menu-button {
        opacity: 1;
    }

    &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

// Global styles to ensure menu appears above everything
::v-deep .p-menu {
    z-index: 9999 !important;
}

::v-deep .p-menu-overlay {
    z-index: 9999 !important;
}
</style>
